import React, { useCallback, useEffect, useState } from 'react';
import sortBy from 'lodash/sortBy';
import { useRouter } from 'next/router';

import { ARTICLE_PAGE_SIZE } from 'lib/constants';

import styled from 'styled-components';
import { PageMainContentId } from 'constants/Global';
import { PageMeta as Meta } from 'components/Meta';
import { ErrorPage, ArticleCardTileList } from 'styled';
import Breadcrumbs from 'styled/components/Breadcrumbs';
import LanguageSelector from 'styled/components/LanguageSelector';
import { sanitizeStaticPath } from 'utils/sanitizeStaticPath';

import useArticles from './hooks/useArticles';

import type { ErrorPage as IErrorPage, IBrowsePage } from 'types';
import type { IBreadcrumbsProps } from 'styled/components/Breadcrumbs';

export interface IBrowsePageViewProps {
  breadcrumbs?: IBreadcrumbsProps['crumbConfig'];
  browsePage: IBrowsePage | null;
  errorPage: IErrorPage;
}

const Banner = styled.div`
  background-color: ${({ theme }) => theme.palette.blue[90]};
  text-align: center;
  padding: 1.5rem 0;

  ${({ theme }) => theme.mediaQueries.sm} {
    padding: 3rem 0;
  }
`;

const Title = styled.h1`
  ${({ theme }) => theme.typography.variants.primary['md-lg']};
  color: white;

  ${({ theme }) => theme.mediaQueries.sm} {
    ${({ theme }) => theme.typography.variants.primary['xl-xxl']};
  }
`;

const LanguageSelectorContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  margin: 1.5rem 2rem 0;

  ${({ theme }) => theme.mediaQueries.sm} {
    margin: 2rem 2.5rem 0;
  }
`;

const BrowsePageView: React.FC<IBrowsePageViewProps> = ({
  browsePage,
  breadcrumbs,
  errorPage,
}) => {
  const { asPath: pathname, query } = useRouter();
  const [language, setLanguage] = useState('en');
  const [hasLanguageChanged, setHasLanguageChanged] = useState(false);

  const pageNumber = Number(query.page) || 1;
  // Fetch articles for given browse page
  const { data: articlesData, isLoading: isArticlesLoading } = useArticles({
    articleType: browsePage?.type,
    hasLanguageChanged,
    isTaxonomyBrowsePage: browsePage?.documentType === 'browsePage',
    pageNumber,
    limit: ARTICLE_PAGE_SIZE,
    language,
    slug: browsePage?.slug,
  });
  const { articles: initialArticles, languages } = browsePage || {};
  /** First page shows initial articles that were SSG
    Subsequent pages, fetch client side.
    Ensure we show fetched data when language changes
    */
  const articles =
    pageNumber == 1 && !hasLanguageChanged
      ? initialArticles
      : articlesData?.articles;
  const totalArticles = Math.ceil(
    articlesData?.count || browsePage?.articleCount || 0 / 10,
  );

  useEffect(() => {
    setLanguage('en');
  }, []);

  const handleLanguageChange = useCallback(
    (language: string) => {
      setLanguage(language);
      setHasLanguageChanged(true);
    },
    [setLanguage],
  );

  if (!browsePage) {
    return <ErrorPage errorPage={errorPage} />;
  }

  return (
    <div id={PageMainContentId} className="relative">
      <Meta
        title={browsePage.hero.title}
        description={browsePage.hero.description}
        image={browsePage.hero.image}
        seo={browsePage.seo}
        pathname={sanitizeStaticPath(pathname)}
      />
      {breadcrumbs && <Breadcrumbs crumbConfig={breadcrumbs} />}
      <Banner>
        <Title>{browsePage.title}</Title>
      </Banner>
      <div className="z-2 relative m-auto max-w-[90rem]">
        <LanguageSelectorContainer>
          <LanguageSelector
            availableLanguages={languages}
            currentLanguage={language}
            onChange={handleLanguageChange}
          />
        </LanguageSelectorContainer>

        <ArticleCardTileList
          // sort by title, but also give preference to articles with images
          articles={sortBy(
            articles?.filter((article) =>
              language ? article.language === language : true,
            ),
            ['title'],
          )}
          articleCount={totalArticles}
          browsePageSlug={browsePage?.slug || ''}
          isLoading={isArticlesLoading}
          pageNumber={pageNumber}
          shouldPaginate={true}
        />
      </div>
    </div>
  );
};

export default BrowsePageView;
